import { State } from 'country-state-city';
import { ValidationError } from './errors';
import { ValidatePaymentInfoForStripeOptions } from './types';

type StripeBillingAddress = Partial<{
  city: string;
  country: string;
  line1: string;
  line2: string;
  postal_code: string;
  state: string;
}>;

const nonempty = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return undefined;
  }
  return trimmed;
};

export const validatePaymentInfoForStripe = async ({
  stripe,
  elements,
  billingDetails,
}: ValidatePaymentInfoForStripeOptions) => {
  if (!stripe || !elements) {
    // Do not allow form submission until stripe.js has loaded
    throw new ValidationError('Failed to load stripe elements');
  }

  // Trigger form validation and wallet collection
  const { error: submitError } = await elements.submit();
  if (submitError) {
    throw new ValidationError(
      `Payment submission failed with error: ${
        submitError.code ?? submitError.message ?? 'Something went wrong'
      }`,
      {
        silent: submitError.type === 'validation_error',
      },
    );
  }

  const customerName = [billingDetails?.firstName, billingDetails?.lastName]
    .map((part) => part?.trim())
    .filter((part): part is string => Boolean(part))
    .join(' ');

  const stateName = billingDetails?.state
    ? State.getStateByCodeAndCountry(
        billingDetails.state,
        billingDetails.country,
      )?.name
    : undefined;

  const city = nonempty(billingDetails?.city);
  const country = nonempty(billingDetails?.country);
  const line1 = nonempty(billingDetails?.address);
  const line2 = nonempty(billingDetails?.unit);
  const postalCode = nonempty(billingDetails?.postalCode);
  const state = nonempty(stateName);

  const address: StripeBillingAddress = {
    ...(city !== undefined ? { city } : {}),
    ...(country !== undefined ? { country } : {}),
    ...(line1 !== undefined ? { line1 } : {}),
    ...(line2 !== undefined ? { line2 } : {}),
    ...(postalCode !== undefined ? { postal_code: postalCode } : {}),
    ...(state !== undefined ? { state } : {}),
  };

  // Create the PaymentMethod
  const { error: stripeError, paymentMethod } =
    await stripe.createPaymentMethod({
      elements,
      params: {
        billing_details: {
          address,
          ...(customerName ? { name: customerName } : {}),
        },
      },
    });

  if (stripeError) {
    throw new ValidationError(
      `Payment method creation failed with error: ${
        stripeError.message ?? 'Something went wrong'
      }`,
    );
  }

  return paymentMethod;
};
